import "../../components/welcomeScreen/welcomeWizard.scss";

import { List } from "immutable";
import { isEqual } from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import { connect } from "react-redux";

import ModalBody from "../../components/modal/ModalBody";
import ModalHeading from "../../components/modal/ModalHeading";
import ModalView from "../../components/modal/ModalView";
import ModalViewStack from "../../components/modal/ModalViewStack";
import PageSlider from "../../components/pageSlider";
import WelcomeWizardFooter from "../../components/welcomeScreen/WelcomeWizardFooter";
import interfaceMessages from "../../constants/interfaceMessages";
import PubSubTopics from "../../constants/pubSubTopics";
import sliderTransitions from "../../constants/sliderTransitions";
import syncApplications from "../../constants/syncApplications";
import urlPrefixes from "../../constants/urlPrefixes";
import dialogs from "../../dialogs";
import batchOperations from "../../modules/batchOperations";
import chainReducer, { batchActions } from "../../modules/chainReducer";
import { definitionSelectors } from "../../modules/definition";
import { itemSelectors } from "../../modules/items";
import syncReducer, { syncOperations } from "../../modules/sync";
import { syncSettingsOperations, syncSettingsSelectors } from "../../modules/sync/settings";
import { uiStateOperations } from "../../modules/uiState";
import { nativeCommsBroadcastMessageTypes } from "../../nativeComms";
import { createFromJS } from "../../types";
import openUrl from "../../utils/openUrl";
import SettingsStrictNarrativeActionModal from "../settingsModal/SettingsStrictNarrativeActionModal";
import SyncApplicationPage from "./SyncApplicationPage";
import SyncFolderSelectionPage from "./SyncFolderSelectionPage";
import SyncPropertyOptionsPage from "./SyncPropertyOptionsPage";
import SyncRelationshipOptionsPage from "./SyncRelationshipOptionsPage";
import SyncScrivenerPreconfiguredPage from "./SyncScrivenerPreconfiguredPage";
import SyncUlyssesPreconfiguredPage from "./SyncUlyssesPreconfiguredPage";

const chainedSyncReducer = chainReducer(syncReducer);

const PRECONFIGURED_PAGE = "PRECONFIGURED_PAGE";
const APP_PAGE = "APP_PAGE";
// const PROJECT_PAGE = "PROJECT_PAGE";
const FOLDER_OPTIONS_PAGE = "FOLDER_OPTIONS_PAGE";
const PROPERTY_OPTIONS_PAGE = "PROPERTY_OPTIONS_PAGE";
const RELATIONSHIP_OPTIONS_PAGE = "RELATIONSHIP_OPTIONS_PAGE";

const renderPreconfiguredPage = (
  app,
  path,
  hasExternalRep,
  ulyssesWaitingForAuth,
  ulyssesTree,
  ulyssesSelectedItemId,
  settingsChanged,
  onNextClick,
  onBackClick,
  onProjectLinkClick,
  onBrowseClick
) => {
  switch (app) {
    case syncApplications.SCRIVENER:
      return (
        <SyncScrivenerPreconfiguredPage
          path={path}
          settingsChanged={settingsChanged}
          hasExternalRep={hasExternalRep}
          onNextClick={onNextClick}
          onBackClick={onBackClick}
          onProjectLinkClick={onProjectLinkClick}
          onBrowseClick={onBrowseClick}
        />
      );
    case syncApplications.ULYSSES:
      return (
        <SyncUlyssesPreconfiguredPage
          ulyssesTree={ulyssesTree}
          ulyssesWaitingForAuth={ulyssesWaitingForAuth}
          ulyssesSelectedItemId={ulyssesSelectedItemId}
          settingsChanged={settingsChanged}
          onNextClick={onNextClick}
          onBackClick={onBackClick}
          onProjectLinkClick={onProjectLinkClick}
          onBrowseClick={onBrowseClick}
        />
      );
    default:
      return null;
  }
};

const renderPreconfiguredPageFooter = (app, hasExternalRep, settingsChanged, onNextClick, onBackClick) => {
  switch (app) {
    case syncApplications.SCRIVENER:
      return (
        <WelcomeWizardFooter
          key="footer"
          showBack={true}
          backText={interfaceMessages.SYNC_REMOVE_CURRENT_SYNC_TITLE}
          onBackClick={onBackClick}
          showDone={false}
          showNext={true}
          nextText={settingsChanged ? "Next" : "Show Options"}
          disableNext={!hasExternalRep}
          showError={!hasExternalRep}
          errorMessage={hasExternalRep ? null : "Waiting to read Scrivener project..."}
          onNextClick={onNextClick}
        />
      );
    case syncApplications.ULYSSES:
      return (
        <WelcomeWizardFooter
          key="footer"
          showBack={true}
          backText="Remove Current Sync"
          onBackClick={onBackClick}
          showDone={false}
          showNext={true}
          nextText={"Next"}
          disableNext={!hasExternalRep}
          showError={!hasExternalRep}
          errorMessage={hasExternalRep ? null : "Waiting to connect to Ulysses..."}
          onNextClick={onNextClick}
        />
      );
    default:
      return null;
  }
};

const renderApplicationPage = onAppClick => <SyncApplicationPage onAppClick={onAppClick} />;

const renderApplicationPageFooter = onDoneClick => (
  <WelcomeWizardFooter
    key="footer"
    showBack={false}
    showDone={true}
    doneText="Close without Syncing"
    onDoneClick={onDoneClick}
    showNext={false}
  />
);

const renderPropertiesOptionsPage = (localStore, isValid, dispatchLocal, onBackClick, onNextClick) => {
  return (
    <SyncPropertyOptionsPage
      localStore={localStore}
      dispatchLocal={dispatchLocal}
      onBackClick={onBackClick}
      onNextClick={onNextClick}
      isValid={isValid}
    />
  );
};

const renderPropertiesOptionsPageFooter = (
  localStore,
  isValid,
  errorMessage,
  onBackClick,
  onNextClick,
  _onDoneClick
) => {
  return (
    <WelcomeWizardFooter
      key="footer"
      showBack={true}
      backText="Back"
      onBackClick={onBackClick}
      showNext={true}
      disableNext={!isValid}
      onNextClick={onNextClick}
      showDone={false}
      showError={!isValid}
      errorMessage={errorMessage}
    />
  );
};

const renderFolderSelectionPage = (
  localStore,
  ulyssesTree,
  ulyssesWaitingForAuth,
  isValid,
  dispatchLocal,
  onBackClick,
  onNextClick
) => {
  return (
    <SyncFolderSelectionPage
      localStore={localStore}
      ulyssesTree={ulyssesTree}
      ulyssesWaitingForAuth={ulyssesWaitingForAuth}
      dispatchLocal={dispatchLocal}
      onBackClick={onBackClick}
      onNextClick={onNextClick}
      isValid={isValid}
    />
  );
};

const renderFolderSelectionPageFooter = (localStore, isValid, errorMessage, onBackClick, onNextClick) => {
  return (
    <WelcomeWizardFooter
      key="footer"
      showBack={true}
      backText="Back"
      onBackClick={onBackClick}
      showNext={true}
      disableNext={!isValid}
      onNextClick={onNextClick}
      showDone={false}
      showError={!isValid}
      errorMessage={errorMessage}
    />
  );
};

const renderRelationshipOptionsPage = (localStore, isValid, dispatchLocal, onBackClick, onDoneClick) => (
  <SyncRelationshipOptionsPage
    localStore={localStore}
    dispatchLocal={dispatchLocal}
    onBackClick={onBackClick}
    onDoneClick={onDoneClick}
    isValid={isValid}
  />
);

const renderRelationshipOptionsPageFooter = (isValid, errorMessage, onBackClick, onDoneClick) => (
  <WelcomeWizardFooter
    key="footer"
    showBack={true}
    backText="Back"
    onBackClick={onBackClick}
    showDone={true}
    disableDone={!isValid}
    onDoneClick={onDoneClick}
    showError={!isValid}
    errorMessage={errorMessage}
  />
);

const getInitialPageIdentifiers = state =>
  syncSettingsSelectors.getSyncEnabled(state) ? [APP_PAGE, PRECONFIGURED_PAGE] : [APP_PAGE];

class SyncSettingsModal extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      localStore: null,
      ulyssesTree: null,
      ulyssesWaitingForAuth: false,
      ulyssesSelectedItems: List(),
      isValid: true,
      errorMessage: null,
      eraseExistingData: false,
      stepHistory: List(getInitialPageIdentifiers(props.localStore)),
      strictNarrativeOptionOpen: false,
      strictNarrativeCorrectiveOption: null
    };
    this.dispatchLocal = this.dispatchLocal.bind(this);
    this.showNextStep = this.showNextStep.bind(this);
    this.onBackClick = this.onBackClick.bind(this);
    this.onAppClick = this.onAppClick.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onDoneClick = this.onDoneClick.bind(this);
    this.onNoSyncClick = this.onNoSyncClick.bind(this);

    this.onPreconfiguredBackClick = this.onPreconfiguredBackClick.bind(this);
    this.onProjectLinkClick = this.onProjectLinkClick.bind(this);

    this.onBrowseResult = this.onBrowseResult.bind(this);
    this.onBrowseClick = this.onBrowseClick.bind(this);
    this.onProjectNextClick = this.onProjectNextClick.bind(this);

    this.onUlyssesAuthorizationRequested = this.onUlyssesAuthorizationRequested.bind(this);
    this.onUlyssesAuthorizationReceived = this.onUlyssesAuthorizationReceived.bind(this);

    this.onUlyssesTreeResult = this.onUlyssesTreeResult.bind(this);
    this.onUlyssesRootClick = this.onUlyssesRootClick.bind(this);
    this.onPropertiesNextClick = this.onPropertiesNextClick.bind(this);
    this.onFolderNextClick = this.onFolderNextClick.bind(this);

    this.onUlyssesStrictNarrativeActionDone = this.onUlyssesStrictNarrativeActionDone.bind(this);
    this.onUlyssesStrictNarrativeActionCancel = this.onUlyssesStrictNarrativeActionCancel.bind(this);
  }

  componentWillMount() {
    this.copyLocalStore(this.props);
  }

  componentDidMount() {
    this._pubSubBrowseToken = PubSub.subscribe(PubSubTopics.syncSettingsBrowseResult, this.onBrowseResult);
    this._pubSubUlyssesTreeToken = PubSub.subscribe(
      PubSubTopics.syncSettingsUlyssesTreeResult,
      this.onUlyssesTreeResult
    );
    this._pubSubUlyssesAuthRequestedToken = PubSub.subscribe(
      PubSubTopics.ulyssesAuthorizationRequested,
      this.onUlyssesAuthorizationRequested
    );
    this._pubSubUlyssesAuthReceivedToken = PubSub.subscribe(
      PubSubTopics.ulyssesAuthorizationReceived,
      this.onUlyssesAuthorizationReceived
    );
    this._pubSubEscapeKeyDownToken = PubSub.subscribe(PubSubTopics.escapeKeyDown, this.onCloseClick);
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubBrowseToken);
    PubSub.unsubscribe(this._pubSubUlyssesTreeToken);
    PubSub.unsubscribe(this._pubSubUlyssesAuthRequestedToken);
    PubSub.unsubscribe(this._pubSubUlyssesAuthReceivedToken);
    PubSub.unsubscribe(this._pubSubEscapeKeyDownToken);
  }

  copyLocalStore(props) {
    if (props.localStore.sync.settings.app === syncApplications.ULYSSES) {
      PubSub.publish(PubSubTopics.sendNativeMessage, {
        msg: nativeCommsBroadcastMessageTypes.REQUEST_ULYSSES_TREE
      });
    }

    this.setState({
      localStore: props.localStore,
      isValid: true,
      errorMessage: null
    });
  }

  dispatchLocal(a) {
    const newStore = this.state.localStore.set(
      "sync",
      chainedSyncReducer(this.state.localStore.sync, a, this.state.localStore)
    );
    const { isValid, errorMessage } = syncSettingsSelectors.isSyncSettingsValid(newStore);
    this.setState({ localStore: newStore, isValid, errorMessage });
    return newStore;
  }

  settingsHaveChanged() {
    return (
      this.state.localStore !== this.props.localStore &&
      !isEqual(this.state.localStore.toJS(), this.props.localStore.toJS())
    );
  }

  onCloseClick() {
    if (this.settingsHaveChanged()) {
      const msg = "Are you sure you want to close without applying your Sync Settings changes?";
      dialogs.okCancelPrompt(msg, result => {
        if (result) {
          this.props.onCloseClick();
        }
      });
    } else {
      this.props.onCloseClick();
    }
  }

  onDoneClick() {
    this.props.onDoneClick(
      this.state.localStore.sync.settings,
      this.state.eraseExistingData,
      this.state.strictNarrativeCorrectiveOption,
      this.props.defaultNarrativeDocumentType,
      this.props.defaultSequenceType
    );
  }

  onBackClick() {
    this.setState({ stepHistory: this.state.stepHistory.pop() });
  }

  onPreconfiguredBackClick() {
    dialogs.okCancelPrompt(interfaceMessages.SYNC_REMOVE_CURRENT_SYNC_MESSAGE, result => {
      if (result) {
        this.onBackClick();
        this.dispatchLocal(syncSettingsOperations.changeEnabled(false));
      }
    });
  }

  showNextStep(stepId) {
    const history = this.state.stepHistory;
    this.setState({
      stepHistory: history.push(stepId)
    });
  }

  appChoiceConfirmed(app, strictNarrativeCorrectiveOption = null, eraseExistingData = false) {
    let stateUpdates = {
      strictNarrativeCorrectiveOption,
      strictNarrativeOptionOpen: false
    };

    if (eraseExistingData) {
      stateUpdates.eraseExistingData = true;
    }

    let actions = [syncSettingsOperations.selectApplication(app)];

    if (eraseExistingData) {
      actions.push(syncSettingsOperations.resetSyncSettings(app));
    }

    if (app == syncApplications.ULYSSES) {
      // clear out the old externalRep
      actions.push(syncOperations.updateExternalRep(null, false));
    } else {
      stateUpdates.ulyssesTree = null;
    }
    this.setState(stateUpdates);
    this.dispatchLocal(batchActions(actions));

    if (app == syncApplications.ULYSSES) {
      // Send a message to request the Ulysses tree
      PubSub.publish(PubSubTopics.sendNativeMessage, {
        msg: nativeCommsBroadcastMessageTypes.REQUEST_ULYSSES_TREE
      });
    }

    this.showNextStep(FOLDER_OPTIONS_PAGE);
  }

  onAppClick(app) {
    if (app === syncApplications.SCRIVENER) {
      // For Scrivener - we prompt them to pick the Scrivener project immediately
      // But we need to set to eraseExistingData now
      this.setState({ eraseExistingData: true });
      // We need to reset our existing sync settings, since this action will result in us choosing
      // a new project.
      this.dispatchLocal(syncSettingsOperations.resetSyncSettings(app));
      // Then show the Open Modal
      this.onBrowseClick();
      return;
    }
    // For Ulysses, check if we need to show a prompt asking about switching to strict
    // folder structure
    const wasStrict = this.state.localStore.definitions.narrativeSettings.strictFolderStructure;
    if (!wasStrict && this.props.isStrictNarrativeViolated) {
      this.setState({
        strictNarrativeOptionOpen: true
      });
      return;
    }
    // The folder structure was already strict, so let the app choice continue
    this.appChoiceConfirmed(app, null, true);
  }

  onUlyssesStrictNarrativeActionDone(strictNarrativeCorrectiveOption) {
    // If they click Ok, then we want to move on to the Ulysses screen
    this.appChoiceConfirmed(syncApplications.ULYSSES, strictNarrativeCorrectiveOption, true);
  }

  onUlyssesStrictNarrativeActionCancel() {
    // If they cancel, revert the setting and advance to the next screen
    this.setState({
      strictNarrativeOptionOpen: false,
      strictNarrativeCorrectiveOption: null
    });
  }

  onNoSyncClick() {
    this.setState({ eraseExistingData: true });
    // Since the dispatchLocal call will not go through straight away (it calls setState, which has indeterminate
    // timing), we grab the final state from the return value instead of getting it from this.state...
    const result = this.dispatchLocal(syncSettingsOperations.selectApplication(syncApplications.NONE));
    this.props.onDoneClick(result.sync.settings, true);
  }

  onBrowseClick() {
    PubSub.publish(PubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.BROWSE_SYNC_FOLDER,
      app: this.state.localStore.sync.settings.app
    });
  }

  onProjectLinkClick() {
    if (this.state.localStore.sync.settings.path) {
      const path = urlPrefixes.LOCAL_PREFIX + this.state.localStore.sync.settings.path;
      openUrl(path);
    }
  }

  onBrowseResult(msg, data) {
    const obj = JSON.parse(data);
    const path = obj.path;
    const isScrivener2 = obj.isScrivener2;
    const externalRep = JSON.parse(obj.externalRep);
    const batchAction = batchActions([
      syncSettingsOperations.selectApplication(syncApplications.SCRIVENER),
      syncSettingsOperations.setProjectPath(path),
      syncOperations.updateExternalRep(externalRep, isScrivener2)
    ]);
    this.dispatchLocal(batchAction);
    // Confirm it is Scrivener
    this.appChoiceConfirmed(syncApplications.SCRIVENER, null, false);
  }

  onUlyssesAuthorizationRequested(_msg, _data) {
    this.setState({ ulyssesWaitingForAuth: true });
  }

  onUlyssesAuthorizationReceived(_msg, _data) {
    this.setState({ ulyssesWaitingForAuth: false });
  }

  onUlyssesTreeResult(msg, data) {
    const ulyssesTree = createFromJS.SyncRepresentationRecord(data);
    const { isValid, errorMessage } = syncSettingsSelectors.isSyncSettingsValid(this.state.localStore);
    this.setState({ ulyssesTree, isValid, errorMessage });
    const batchAction = batchActions([
      syncSettingsOperations.selectApplication(syncApplications.ULYSSES),
      syncOperations.updateExternalRep(ulyssesTree, false)
    ]);
    this.dispatchLocal(batchAction);
  }

  onUlyssesRootClick(id) {
    this.dispatchLocal(syncSettingsOperations.setNarrativeFolderId(id));
  }

  onProjectNextClick() {
    this.showNextStep(FOLDER_OPTIONS_PAGE);
    // this.showNextStep(PROPERTY_OPTIONS_PAGE);
  }

  onFolderNextClick() {
    this.showNextStep(PROPERTY_OPTIONS_PAGE);
  }

  onPropertiesNextClick() {
    this.showNextStep(RELATIONSHIP_OPTIONS_PAGE);
  }

  renderSyncSettingsModal() {
    const currentStep = this.state.stepHistory.last();
    let content = null;
    let footer = null;
    let title = "Sync Settings";
    let pageIndex = 0;
    let showCancel = false;

    const hasExternalRepOrUlyssesTree =
      this.state.localStore.sync.settings.app === syncApplications.SCRIVENER
        ? Boolean(this.state.localStore.sync.externalRep)
        : Boolean(this.state.ulyssesTree);

    switch (currentStep) {
      case APP_PAGE:
        content = renderApplicationPage(this.onAppClick);
        footer = renderApplicationPageFooter(this.onNoSyncClick);
        title = "Select Sync Application";
        pageIndex = 1;
        showCancel = true;
        break;
      case PRECONFIGURED_PAGE:
        content = renderPreconfiguredPage(
          this.state.localStore.sync.settings.app,
          this.state.localStore.sync.settings.path,
          Boolean(this.state.localStore.sync.externalRep),
          this.state.ulyssesWaitingForAuth,
          this.state.ulyssesTree,
          this.state.localStore.sync.settings.narrativeFolderId,
          this.settingsHaveChanged(),
          this.onProjectNextClick,
          this.onPreconfiguredBackClick,
          this.onProjectLinkClick,
          this.onBrowseClick
        );
        footer = renderPreconfiguredPageFooter(
          this.state.localStore.sync.settings.app,
          hasExternalRepOrUlyssesTree,
          this.settingsHaveChanged(),
          this.onProjectNextClick,
          this.onPreconfiguredBackClick
        );
        title = "Current Sync Project";
        pageIndex = 2;
        showCancel = true;
        break;
      case PROPERTY_OPTIONS_PAGE:
        content = renderPropertiesOptionsPage(
          this.state.localStore,
          this.state.isValid,
          this.dispatchLocal,
          this.onBackClick,
          this.onPropertiesNextClick,
          this.onDoneClick
        );
        footer = renderPropertiesOptionsPageFooter(
          this.state.localStore,
          this.state.isValid,
          this.state.errorMessage,
          this.onBackClick,
          this.onPropertiesNextClick,
          this.onDoneClick
        );
        title = "Sync Properties";
        pageIndex = 3;
        showCancel = true;
        break;
      case FOLDER_OPTIONS_PAGE:
        content = renderFolderSelectionPage(
          this.state.localStore,
          this.state.ulyssesTree,
          this.state.ulyssesWaitingForAuth,
          this.state.isValid,
          this.dispatchLocal,
          this.onBackClick,
          this.onFolderNextClick
        );
        footer = renderFolderSelectionPageFooter(
          this.state.localStore,
          this.state.isValid,
          this.state.errorMessage,
          this.onBackClick,
          this.onFolderNextClick
        );
        title = "Select Sync Folders";
        pageIndex = 3;
        showCancel = true;
        break;
      case RELATIONSHIP_OPTIONS_PAGE:
        content = renderRelationshipOptionsPage(
          this.state.localStore,
          this.state.isValid,
          this.dispatchLocal,
          this.onBackClick,
          this.onDoneClick
        );
        footer = renderRelationshipOptionsPageFooter(
          this.state.isValid,
          this.state.errorMessage,
          this.onBackClick,
          this.onDoneClick
        );
        title = "Sync Relationships";
        pageIndex = 4;
        showCancel = true;
        break;
      default:
        break;
    }

    const header = <ModalHeading title={title} showCancel={showCancel} onCancelClick={this.onCloseClick} />;

    return (
      <ModalView className="syncSettingsWizardModal" header={header} footer={footer} fixedHeight={580}>
        <PageSlider pageIndex={pageIndex} pageId={currentStep} transition={sliderTransitions.DEFAULT}>
          <ModalBody>{content}</ModalBody>
        </PageSlider>
      </ModalView>
    );
  }

  renderStrictNarrativeActionModal() {
    const explanation =
      "Syncing with Ulysses requires strict Narrative mode, which only allows folders to have narrative children. " +
      "What would you like to happen to any documents that are currently nested under documents rather than folders?";
    return (
      <SettingsStrictNarrativeActionModal
        explanation={explanation}
        onDoneClick={this.onUlyssesStrictNarrativeActionDone}
        onCloseClick={this.onUlyssesStrictNarrativeActionCancel}
      />
    );
  }

  render() {
    return (
      <ModalViewStack>
        {this.renderSyncSettingsModal()}
        <React.Fragment>
          {this.state.strictNarrativeOptionOpen ? this.renderStrictNarrativeActionModal() : null}
        </React.Fragment>
      </ModalViewStack>
    );
  }
}

SyncSettingsModal.propTypes = {
  localStore: PropTypes.object.isRequired,
  label: PropTypes.string,
  onDoneClick: PropTypes.func.isRequired,
  onCloseClick: PropTypes.func.isRequired,
  isStrictNarrativeViolated: PropTypes.bool.isRequired,
  defaultNarrativeDocumentType: PropTypes.string,
  defaultSequenceType: PropTypes.string.isRequired
};

const mapStateToProps = state => {
  return {
    localStore: state.present,
    isStrictNarrativeViolated: itemSelectors.getParentIdsViolatingStrictNarrativeStructure(state.present).size > 0,
    defaultNarrativeDocumentType: definitionSelectors.getDefaultNarrativeDocumentType(state.present),
    defaultSequenceType: definitionSelectors.getDefaultSequenceType(state.present)
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onDoneClick: (
      syncSettings,
      eraseExistingData,
      strictNarrativeCorrectiveOption,
      defaultNarrativeDocumentType,
      defaultSequenceType
    ) => {
      dispatch(
        batchOperations.applySyncSettings(
          syncSettings,
          eraseExistingData,
          strictNarrativeCorrectiveOption,
          defaultNarrativeDocumentType,
          defaultSequenceType
        )
      );
      PubSub.publish(PubSubTopics.sendNativeMessage, {
        msg: nativeCommsBroadcastMessageTypes.SYNC_RELOAD_REQUIRED
      });
    },
    onCloseClick: () => {
      dispatch(uiStateOperations.hideSyncSettingsModal());
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SyncSettingsModal);
